import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Calculator,
  Plus,
  Minus,
  Trash2,
  Send,
  MessageCircle,
  Palette,
  ChevronRight,
} from "lucide-react";

function CartItem({ item, idx, onUpdateQty, onRemove }) {
  return (
    <div className="flex items-center justify-between text-sm gap-2 p-3 rounded-sm border border-[var(--mp-line)]">
      <div className="flex items-center gap-3">
        <div
          className="w-7 h-7 rounded-sm border border-[var(--mp-line)]"
          style={{ backgroundColor: item.colour_hex }}
        />
        <div>
          <span
            className="font-medium text-xs"
            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
          >
            #{item.colour_number}
          </span>
          <span className="text-[10px] ml-2" style={{ color: "var(--mp-muted)" }}>
            {item.fabric_name} ({item.panna})
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onUpdateQty(idx, -item.meters_per_bundle)}
          className="w-6 h-6 rounded-full border border-[var(--mp-line)] flex items-center justify-center hover:bg-gray-50"
        >
          <Minus className="w-3 h-3" />
        </button>
        <span
          className="text-xs font-semibold w-14 text-center"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {item.quantity_meters}m<br />
          <span className="text-[9px] font-normal" style={{ color: "var(--mp-muted)" }}>
            {Math.ceil(item.quantity_meters / item.meters_per_bundle)} bndl
          </span>
        </span>
        <button
          onClick={() => onUpdateQty(idx, item.meters_per_bundle)}
          className="w-6 h-6 rounded-full border border-[var(--mp-line)] flex items-center justify-center hover:bg-gray-50"
        >
          <Plus className="w-3 h-3" />
        </button>
        <button onClick={() => onRemove(idx)} className="ml-2">
          <Trash2 className="w-4 h-4" style={{ color: "#dc2626" }} />
        </button>
      </div>
    </div>
  );
}

function CalcSummary({ totals }) {
  const { totalMeters, totalBundles } = totals;
  const stats = [
    { value: `${totalMeters}m`, label: "Total Meters", color: "var(--mp-text-strong)" },
    { value: totalBundles, label: "Bundles", color: "var(--mp-text-strong)" },
    { value: "5%", label: "GST Extra", color: "var(--mp-copper)" },
  ];
  return (
    <div
      className="bg-[var(--mp-surface)] border border-[var(--mp-line)] rounded-sm p-4 mb-4"
      data-testid="calculation-summary"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <span
              className="text-lg font-medium block"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: s.color }}
            >
              {s.value}
            </span>
            <span
              className="text-[10px] uppercase tracking-wider"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
      <p
        className="text-[10px] text-center mt-3 font-medium"
        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
      >
        Transport / local lorry Rs. 100 per parcel | Bus / travels Rs. 150 per parcel | GST 5% applicable on fabric value
        at billing
      </p>
    </div>
  );
}

function ActionButtons({ token, sending, onSubmit, onWhatsApp, onInstagram }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {token ? (
        <Button
          onClick={onSubmit}
          disabled={sending}
          className="text-white text-sm py-5 rounded-sm"
          style={{ backgroundColor: "var(--mp-solid)" }}
          data-testid="submit-order-btn"
        >
          {sending ? "Placing..." : "Place Order"} <Send className="w-4 h-4 ml-2" />
        </Button>
      ) : (
        <Link to="/login" className="block">
          <Button
            className="w-full text-white text-sm py-5 rounded-sm"
            style={{ backgroundColor: "var(--mp-solid)" }}
            data-testid="login-to-order-btn"
          >
            Login to Order <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      )}
      <Button
        onClick={onWhatsApp}
        className="text-white text-sm py-5 rounded-sm"
        style={{ backgroundColor: "#25D366" }}
        data-testid="whatsapp-order-btn"
      >
        <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Order
      </Button>
      <Button
        onClick={onInstagram}
        variant="outline"
        className="text-sm py-5 rounded-sm"
        style={{ borderColor: "#E1306C", color: "#E1306C" }}
        data-testid="instagram-order-btn"
      >
        <Palette className="w-4 h-4 mr-2" /> DM on Instagram
      </Button>
    </div>
  );
}

export default function OrderSummaryPanel({
  cart,
  totals,
  token,
  sending,
  selectedFamily,
  onUpdateQty,
  onRemove,
  onSubmit,
  onWhatsApp,
  onInstagram,
  onHide,
  bottomOffset = 0,
  sticky = true,
}) {
  return (
    <div
      className={`${sticky ? "sticky" : ""} bg-white border-t-2 border-[var(--mp-solid)] rounded-t-sm p-5 shadow-2xl mt-6`}
      style={{ bottom: sticky ? bottomOffset : undefined }}
      data-testid="order-summary-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-lg font-medium flex items-center gap-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          <Calculator className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
          Order Summary
        </h3>
        <button onClick={onHide} className="text-xs underline" style={{ color: "var(--mp-muted)" }}>
          {selectedFamily ? "" : "Hide"}
        </button>
      </div>

      <div className="max-h-48 overflow-y-auto space-y-2 mb-4">
        {cart.map((item, idx) => (
          <CartItem
            key={`${item.colour_number}-${item.fabric_quality}`}
            item={item}
            idx={idx}
            onUpdateQty={onUpdateQty}
            onRemove={onRemove}
          />
        ))}
      </div>

      <CalcSummary totals={totals} />
      <ActionButtons
        token={token}
        sending={sending}
        onSubmit={onSubmit}
        onWhatsApp={onWhatsApp}
        onInstagram={onInstagram}
      />
    </div>
  );
}
